import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { IoMailOutline, IoLockClosedOutline, IoEyeOutline, IoEyeOffOutline, IoAlertCircleOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import Wordmark from "../../components/common/Wordmark";
import { accountApi } from "../../services/accountApi";

const MailIcon = IoMailOutline as any;
const LockIcon = IoLockClosedOutline as any;
const EyeIcon = IoEyeOutline as any;
const EyeOffIcon = IoEyeOffOutline as any;
const AlertIcon = IoAlertCircleOutline as any;
const CheckIcon = IoCheckmarkCircleOutline as any;

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  // Set when the password is correct but no longer meets the password policy
  // (Firebase "force upgrade on sign-in"); the user must reset it to get in.
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const [resetMsg, setResetMsg] = useState<
    { type: "success" | "error"; text: string } | null
  >(null);

  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError("");
      setNeedsUpgrade(false);
      setLoading(true);
      await login(email, password);
    } catch (error: any) {
      console.error("Login error:", error);
      if (error?.code === "auth/password-does-not-meet-requirements") {
        setNeedsUpgrade(true);
      } else if (error?.code === "auth/too-many-requests") {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setError("Failed to log in. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setNeedsUpgrade(false);
    setResetMsg(null);
    if (!email.trim()) {
      setResetMsg({
        type: "error",
        text: "Enter your email address above, then click Forgot password.",
      });
      return;
    }
    try {
      setResetLoading(true);
      const result = await accountApi.resendInvite(email.trim());
      setResetMsg({ type: result.ok ? "success" : "error", text: result.message });
    } catch (err) {
      console.error("Password reset request failed:", err);
      setResetMsg({
        type: "error",
        text: "Couldn't reach the server. Please try again.",
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-paper-200">
      {/* Left side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-hero text-on-hero"
        style={{
          backgroundImage:
            "radial-gradient(circle at 0% 0%, rgba(76, 175, 80, 0.35), transparent 55%), radial-gradient(circle at 100% 100%, rgba(113, 238, 184, 0.18), transparent 55%)",
        }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Wordmark className="text-3xl text-on-hero [&_.fy]:text-brand-500" />

          <div className="max-w-md">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-mint mb-4">
              Analytics workbench
            </p>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight mb-6">
              Human mobility analytics,
              <br />
              <span className="text-brand-500">made simple.</span>
            </h1>
            <p className="text-lg text-on-hero/70 max-w-md">
              Mobile network signals, turned into mobility intelligence —
              for smarter cities, sharper transport planning, and better
              decisions under pressure.
            </p>
          </div>

          <div className="flex items-center gap-5 text-[11px] font-mono uppercase tracking-[0.18em] text-on-hero/40">
            <span>Activities</span>
            <span className="h-px w-5 bg-on-hero/15" />
            <span>Origin – Destination</span>
            <span className="h-px w-5 bg-on-hero/15" />
            <span>Real Time</span>
            <span className="h-px w-5 bg-on-hero/15" />
            <span>Flow</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden mb-8 text-center">
            <Wordmark className="text-3xl" />
          </div>

          <div className="bg-surface rounded-3xl shadow-card p-8 sm:p-10 border border-paper-300/70">
            <div className="mb-8">
              <h2 className="font-display text-3xl font-bold text-ink mb-2">
                Sign in
              </h2>
              <p className="text-ink-muted">
                Enter your credentials to continue.
              </p>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-2 rounded-xl bg-bad-soft border border-bad-line px-4 py-3 text-sm text-bad-ink animate-fade-in">
                <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {needsUpgrade && (
              <div className="mb-6 rounded-xl bg-warn-soft border border-warn-line px-4 py-3 text-sm text-warn-ink animate-fade-in">
                <div className="flex items-start gap-2">
                  <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>
                    Our password requirements have changed and your password needs to be updated
                    before you can sign in. We'll email you a link to set a new one.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  className="mt-3 ml-7 font-semibold text-warn-ink underline hover:no-underline disabled:opacity-50"
                >
                  {resetLoading ? "Sending…" : "Email me a link"}
                </button>
              </div>
            )}

            {resetMsg && (
              <div
                className={`mb-6 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm animate-fade-in ${
                  resetMsg.type === "success"
                    ? "bg-brand-50 border-brand-500/30 text-brand-700"
                    : "bg-bad-soft border-bad-line text-bad-ink"
                }`}
              >
                {resetMsg.type === "success" ? (
                  <CheckIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                )}
                <span>{resetMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-ink-soft mb-1.5"
                >
                  Email Address
                </label>
                <div className="relative">
                  <MailIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full rounded-xl border border-paper-300 bg-surface pl-11 pr-4 py-3 text-ink-soft placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition duration-200 disabled:opacity-50"
                    placeholder="you@example.com"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-ink-soft mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <LockIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full rounded-xl border border-paper-300 bg-surface pl-11 pr-12 py-3 text-ink-soft placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition duration-200 disabled:opacity-50"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-ink-faint hover:text-ink-muted hover:bg-paper-200 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-paper-400 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-ink-muted">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading || loading}
                  className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors disabled:opacity-50"
                >
                  {resetLoading ? "Sending…" : "Forgot password?"}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-brand-500 px-4 py-3.5 text-base font-semibold text-on-brand shadow-brand-glow transition-all duration-300 hover:bg-brand-600 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-ink-muted">
              Don't have an account?{" "}
              <a
                href="mailto:sales@telcofy.ai"
                className="font-medium text-brand-600 hover:text-brand-700 transition-colors"
              >
                Contact sales@telcofy.ai
              </a>
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-ink-faint">
            By signing in you agree to the Telcofy terms of use.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
